/**
 * ============================================================================
 * Google Analytics 4 — consent-gated, and off by default. Added D-104.
 * ============================================================================
 *
 * OWNER DECISION, 2026-09-02: the owner asked to see visitor counts and reach,
 * was shown the trade-off between GA4 and a cookieless alternative, and chose
 * GA4 for its depth and its Search Console link. This module implements that
 * choice in the form the rest of the site's posture requires.
 *
 * WHAT WAS SAID BACK, BECAUSE IT SHAPED WHAT GOT BUILT. The ask was "track who
 * all has visited". No web analytics identifies visitors by name, and building
 * something that tried would be both ineffective and squarely against the
 * owner's own DPDPA and PHI-governance positioning. What this gives is what GA4
 * gives: visitor and session counts, pages viewed, referrers (LinkedIn included),
 * countries, and devices. Aggregate, never named.
 *
 * ----------------------------------------------------------------------------
 * THE THREE PROPERTIES THIS FILE EXISTS TO GUARANTEE
 * ----------------------------------------------------------------------------
 * 1. **Nothing reaches Google before an explicit accept.** No `<script>` tag in
 *    the pre-rendered HTML, no `gtag.js` in the bundle's import graph, no
 *    request on load. The tag is injected by `inject()`, which only `grantConsent`
 *    and `restoreConsent` call, so
 *    a visitor who ignores or declines the banner produces zero Google traffic.
 *    That is stricter than Consent Mode's own default, which still loads the
 *    library and sends cookieless pings.
 * 2. **No measurement ID, no analytics.** The ID comes from
 *    `VITE_GA_MEASUREMENT_ID` at build time. It is not committed — an ID in the
 *    repository is an ID that reports from any fork and any stray local build,
 *    into a property that is the owner's rather than the code's. (It is not a
 *    secret: a GA4 measurement ID ships in the HTML of every site that uses
 *    one, and this one was public in `index.html` for as long as it was there.)
 *    With it unset, `isConfigured` is false, the banner never renders and
 *    `inject` returns immediately — which is the state every gate in this
 *    repository runs against, so the sweeps measure the site rather than a tag.
 * 3. **Withdrawal is as easy as consent.** Required by the DPDPA and the GDPR
 *    alike, and the reason `Footer` carries an "Analytics preferences" control
 *    rather than the banner being a one-time gate.
 *
 * WHY THE CHOICE LIVES IN `localStorage` AND NOT A COOKIE. A cookie storing
 * "this visitor declined cookies" is the joke that writes itself, and it would
 * be sent to the server on every request for no reason. The value is read in a
 * try/catch because a private window, a browser configured to block site data,
 * and a thumbnailing context can each make the accessor itself throw.
 */

/** The three states. `unset` is not "declined" — it is "has not been asked". */
export type ConsentState = "granted" | "denied" | "unset";

const STORAGE_KEY = "analytics-consent";

/**
 * The GA4 measurement ID, from the build environment.
 *
 * `import.meta.env` is statically replaced by Vite, so an unconfigured build
 * carries the empty string here and `isConfigured()` is false forever: no
 * banner, no injection, no request. The measurement ID itself cannot leak into
 * a build that was not configured to carry it, which is the property worth
 * having.
 *
 * IT IS NOT TREE-SHAKEN, AND AN EARLIER DRAFT OF THIS COMMENT SAID IT WAS.
 * Measured rather than assumed, and the assumption was wrong: `inject()` is
 * reachable from an exported `grantConsent`, and `isConfigured()` is a regex
 * test Rollup cannot fold, so the string "googletagmanager.com" IS in the main
 * chunk of an unconfigured build. It is unreachable at runtime, not absent from
 * the file. The claims that survive measurement are narrower and are the ones
 * the gates assert: **no analytics markup in any served HTML document**, and
 * **no request to Google before an explicit accept**, read off the network
 * rather than off the bundle (D-073's standard for the video facade).
 */
const MEASUREMENT_ID: string = import.meta.env.VITE_GA_MEASUREMENT_ID ?? "";

/** Whether this build carries a measurement ID at all. */
export const isConfigured = (): boolean => /^G-[A-Z0-9]{6,}$/.test(MEASUREMENT_ID);

/* eslint-disable @typescript-eslint/no-explicit-any */
type Gtag = (...args: any[]) => void;

declare global {
	interface Window {
		dataLayer?: any[];
		gtag?: Gtag;
	}
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * The stored choice, or `unset`.
 *
 * Returns `unset` rather than throwing when storage is unavailable, which means
 * a visitor in a locked-down browser is treated as not-yet-asked and therefore
 * as not-consented. Failing closed is the only safe direction here.
 */
export const readConsent = (): ConsentState => {
	try {
		const stored = window.localStorage.getItem(STORAGE_KEY);
		return stored === "granted" || stored === "denied" ? stored : "unset";
	} catch {
		return "unset";
	}
};

const writeConsent = (state: ConsentState): void => {
	try {
		if (state === "unset") window.localStorage.removeItem(STORAGE_KEY);
		else window.localStorage.setItem(STORAGE_KEY, state);
	} catch {
		/* A visitor who cannot persist the choice is asked again next visit.
		   That is the correct failure: the alternative is treating an unstorable
		   browser as consenting. */
	}
};

let injected = false;

/**
 * Injects `gtag.js` and configures it. Called only from `grantConsent`.
 *
 * `anonymize_ip` is not set: GA4 anonymises IP addresses for every event as a
 * matter of course and the parameter is a no-op it inherited from Universal
 * Analytics. Setting it would look like a control and be none.
 */
const inject = (): void => {
	if (injected || !isConfigured()) return;
	injected = true;

	window.dataLayer = window.dataLayer || [];
	/* The canonical gtag shim. It must push `arguments` itself rather than a
	   rest array — gtag.js reads the `arguments` object's shape. */
	// eslint-disable-next-line prefer-rest-params, @typescript-eslint/no-explicit-any
	window.gtag = function gtag(): void {
		// eslint-disable-next-line prefer-rest-params
		window.dataLayer?.push(arguments);
	} as Gtag;

	window.gtag("js", new Date());
	/* Consent Mode v2 defaults, set BEFORE config. Only analytics storage is
	   granted — this site runs no advertising and asking for ad consent it
	   would never use is asking for more than is needed. */
	window.gtag("consent", "default", {
		ad_storage: "denied",
		ad_user_data: "denied",
		ad_personalization: "denied",
		analytics_storage: "granted"
	});
	window.gtag("config", MEASUREMENT_ID, {
		/* Page views are sent by `trackPageView` on every route change, because
		   this is a single-page app: the automatic pageview fires once, on the
		   first document, and every client-side navigation after it would go
		   uncounted. */
		send_page_view: false
	});

	const script = document.createElement("script");
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
	document.head.appendChild(script);
};

/** Records the accept, injects the tag, and counts the page the visitor is on. */
export const grantConsent = (path: string): void => {
	writeConsent("granted");
	inject();
	trackPageView(path);
};

/**
 * Records the decline. The tag is never injected, so there is nothing to tear
 * down — which is the point of not loading it up front.
 *
 * A visitor who declines AFTER accepting in an earlier session keeps whatever
 * `_ga` cookie that session set; this cannot remove a cookie on a Google
 * domain, and claiming otherwise would be the kind of control that looks like
 * one and is none. The honest statement is in the banner copy.
 */
export const denyConsent = (): void => writeConsent("denied");

/**
 * The event the footer control fires so the banner re-opens without a reload.
 *
 * A DOM event rather than a context or a store: two components, one direction,
 * no shared state to keep in sync — and it costs the app nothing when analytics
 * is unconfigured, because nothing is listening.
 */
export const CONSENT_RESET_EVENT = "analytics-consent-reset";

/**
 * Clears the choice so the banner asks again. This is the withdrawal path, and
 * it is why `Footer` carries a control at all: under both the DPDPA and the
 * GDPR, taking consent back has to be as easy as giving it, and a banner that
 * only ever appears once is not that.
 */
export const resetConsent = (): void => {
	writeConsent("unset");
	window.dispatchEvent(new Event(CONSENT_RESET_EVENT));
};

/** Sends one page view. A no-op unless the tag was injected. */
export const trackPageView = (path: string): void => {
	if (!injected || !window.gtag) return;
	window.gtag("event", "page_view", {
		page_path: path,
		page_location: window.location.href,
		page_title: document.title
	});
};

/**
 * Re-injects on a later visit by a visitor who already accepted.
 *
 * Called once from the banner's mount effect. It is what makes the accept
 * durable without the tag ever being in the initial import graph.
 */
export const restoreConsent = (path: string): void => {
	if (readConsent() !== "granted") return;
	inject();
	trackPageView(path);
};
